import { Box, IconButton, TextField, Tooltip } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import UndoIcon from "@material-ui/icons/Undo";
import { Button, ContainerPage, TitlePage } from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import assetService from "services/assetsService";
import { KeyboardDatePicker, Table } from "shared";
import { formatDate, formatDateToBR } from "utils";
import { dateToday } from "utils/dateToday";

import { useStyles } from "./styles";

const getInitialParams = search => {
  const searchParams = new URLSearchParams(search);

  return {
    date: searchParams.get("date") || dateToday(),
    search: searchParams.get("search") || undefined
  };
};

const AssetsDailyCount = () => {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const { addPopup } = usePopup();
  const [editableRows, setEditableRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [recalcKey, setRecalcKey] = useState(0);
  const loadedDateRef = useRef(null);
  const initialParams = useMemo(
    () => getInitialParams(location.search),
    [location.search]
  );

  const { data, onChange, params, refresh, status } = useTableParams({
    service: ({ search, ...requestParams }) =>
      assetService.getAssetsDailyCount(requestParams),
    paramsDefault: {
      date: initialParams.date,
      search: initialParams.search,
      page: 1,
      pageSize: 10,
      orderDirection: "-date"
    }
  });

  const updateSearchParams = nextParams => {
    const mergedParams = {
      ...params,
      ...nextParams
    };
    const searchParams = new URLSearchParams();

    searchParams.set("date", mergedParams.date);

    if (mergedParams.search) {
      searchParams.set("search", mergedParams.search);
    }

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const handleChange = nextParams => {
    updateSearchParams(nextParams);
    onChange(nextParams);
  };

  const handleSearch = event => {
    handleChange({
      page: 1,
      search: event.target.value?.trim()
    });
  };

  const handleDateChange = date => {
    setEditableRows([]);
    setOriginalRows([]);
    loadedDateRef.current = null;
    handleChange({
      page: 1,
      date: formatDate(date)
    });
  };

  const handleQuantityChange = (assetName, rawValue) => {
    const value = rawValue.trim() === "" ? 0 : Number(rawValue);

    if (!Number.isInteger(value) || value < 0) {
      setRecalcKey(key => key + 1);
      return;
    }

    setEditableRows(rows =>
      rows.map(row =>
        row.assetName === assetName ? { ...row, quantity: value } : row
      )
    );

    if (rawValue.trim() === "") {
      setRecalcKey(key => key + 1);
    }
  };

  const handleResetQuantity = assetName => {
    const original = originalRows.find(row => row.assetName === assetName);

    setEditableRows(rows =>
      rows.map(row =>
        row.assetName === assetName
          ? { ...row, quantity: original?.quantity ?? 0 }
          : row
      )
    );
    setRecalcKey(key => key + 1);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = editableRows.map(row => ({
        date: params.date,
        assetName: row.assetName,
        quantity: row.quantity === "" ? 0 : Number(row.quantity)
      }));

      const invalidQuantity = payload.some(
        row => !Number.isInteger(row.quantity) || row.quantity < 0
      );

      if (invalidQuantity) {
        addPopup({
          type: "error",
          title: "Informe apenas quantidades inteiras maiores ou iguais a zero"
        });
        return;
      }

      const { data: savedRows } = await assetService.updateAssetsDailyCount(
        payload
      );

      setEditableRows(savedRows);
      setOriginalRows(savedRows);
      addPopup({
        type: "success",
        title: "Quantidades atualizadas com sucesso"
      });
    } catch (err) {
      if (err?.response?.status !== 401) {
        addPopup({
          type: "error",
          title: "Erro ao salvar quantidades"
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!new URLSearchParams(location.search).get("date")) {
      updateSearchParams({ date: initialParams.date });
    }
  }, []);

  useEffect(() => {
    const dataMatchesSelectedDate =
      data.length > 0 && data.every(item => item.date === params.date);

    if (
      dataMatchesSelectedDate &&
      (loadedDateRef.current !== params.date || editableRows.length === 0)
    ) {
      setEditableRows(data);
      setOriginalRows(data);
      loadedDateRef.current = params.date;
    }
  }, [data, params.date, editableRows.length]);

  const rows = useMemo(() => {
    const search = params.search?.toLowerCase().trim();

    return editableRows
      .map(row => {
        const original = originalRows.find(
          item => item.assetName === row.assetName
        );

        return {
          ...row,
          isEdited:
            Number(row.quantity || 0) !== Number(original?.quantity || 0)
        };
      })
      .filter(row =>
        search ? row.assetName.toLowerCase().includes(search) : true
      );
  }, [editableRows, originalRows, params.search]);

  const columns = [
    {
      field: "date",
      label: "Data",
      align: "center",
      disabledSort: true,
      style: { minWidth: "140px" },
      formatter: value => formatDateToBR(value)
    },
    {
      field: "assetName",
      label: "Ativo",
      disabledSort: true,
      style: { minWidth: "220px", maxWidth: "360px" }
    },
    {
      field: "quantity",
      label: "Quantidade",
      align: "center",
      disabledSort: true,
      style: { minWidth: "170px" },
      renderCell: ({ data: row }) => {
        return (
          <Box className={classes.quantityCell}>
            <Box className={classes.undoSlot}>
              <Tooltip
                title="Resetar quantidade"
                arrow
                disableHoverListener={!row.isEdited}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleResetQuantity(row.assetName)}
                    disabled={!row.isEdited || isSaving}
                    style={{
                      color: "#bbb",
                      padding: 2,
                      visibility: row.isEdited ? "visible" : "hidden"
                    }}
                  >
                    <UndoIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TextField
              key={`quantity-${row.assetName}-${recalcKey}-${row.quantity}`}
              className={classes.quantityInput}
              variant="outlined"
              size="small"
              defaultValue={row.quantity}
              disabled={isSaving}
              onBlur={event =>
                handleQuantityChange(row.assetName, event.target.value)
              }
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.target.blur();
                  return;
                }

                const controlKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Home",
                  "End"
                ];
                const isDigit = /^\d$/.test(event.key);
                const isControl =
                  controlKeys.includes(event.key) ||
                  event.ctrlKey ||
                  event.metaKey;

                if (!isDigit && !isControl) event.preventDefault();
              }}
              inputProps={{ style: { textAlign: "right" } }}
            />
            <Box className={classes.undoSlot} />
          </Box>
        );
      }
    }
  ];

  return (
    <>
      <ContainerPage>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <TitlePage>Contagem diária de ativos</TitlePage>
        </Box>

        <Table
          filters={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flex="1"
              style={{ gap: "1rem" }}
            >
              <Box maxWidth="170px">
                <KeyboardDatePicker
                  fullWidth
                  disableToolbar
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  value={new Date(`${params.date}T00:00:00`)}
                  onChange={handleDateChange}
                  views={["date"]}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSave}
                isLoading={isSaving}
                disabled={status === "pending" || editableRows.length === 0}
              >
                Salvar quantidades
              </Button>
            </Box>
          }
          handleSearch={handleSearch}
          columns={columns}
          data={rows}
          onChange={handleChange}
          params={params}
          status={status}
          refresh={refresh}
          placeholder="Ativo"
          count={rows.length}
          size="small"
          hover
          paginationServer={false}
          rowStyle={row => (row.isEdited ? { background: "#fffde7" } : {})}
        />
      </ContainerPage>
    </>
  );
};

export default AssetsDailyCount;
